import { isPowerOperator } from "@/models/utils/semanticChecks";

import type { NodeJson } from "@corca/data";

// rewrite of ExpressionRenderVisitor for NodeJson
export function nodeToExpressionText(node: NodeJson, options: { normalizeExp?: boolean }): string {
  switch (node.type) {
    case "group": {
      const contentNode = node.children.find((n) => n.type === "inline" || n.type === "matrix");
      return `group(${contentNode ? nodeToExpressionText(contentNode, options) : ""})`;
    }
    case "operator": {
      const params = node.children.map((n) => nodeToExpressionText(n, options)).join(", ");
      return `${node.props.operatorType}(${params})`;
    }
    case "operatorSymbol": {
      const contentNode = node.children[0];
      return `opsym(${contentNode ? nodeToExpressionText(contentNode, options) : ""})`;
    }
    case "label": {
      const contentNode = node.children.find((n) => n.type === "inline");
      return `label(${contentNode ? nodeToExpressionText(contentNode, options) : ""})`;
    }
    case "symbol": {
      let { value } = node.props;
      if (node.children) {
        const decorators = node.children.filter((n) => n.type === "decorator");
        if (decorators.length > 0) {
          value = decorators.reduce((acc, decorator) => {
            return `${nodeToExpressionText(decorator, options)}(${acc})`;
          }, value);
        }
        const labels = node.children.filter((n) => n.type === "label");
        if (labels.length > 0) {
          value += `, ${labels.map((n) => nodeToExpressionText(n, options)).join(", ")}`;
        }
      }
      return `sym(${value})`;
    }
    case "number": {
      const unitNode = node.children.find((n) => n.type === "unit");
      return `num(${node.props.value}${unitNode ? `, ${nodeToExpressionText(unitNode, options)}` : ""})`;
    }
    case "variable": {
      const params = node.children.map((n) => nodeToExpressionText(n, options)).join(", ");
      return `var(${params})`;
    }
    case "text": {
      const params = node.children.map((n) => nodeToExpressionText(n, options)).join(", ");
      return params;
    }
    case "wrapper": {
      const contentNode = node.children.find((n) => n.type === "inline");
      return contentNode ? nodeToExpressionText(contentNode, options) : "";
    }
    case "constant": {
      const contentNode = node.children[0];
      return `const(${contentNode ? nodeToExpressionText(contentNode, options) : ""})`;
    }
    case "function": {
      const params = node.children.map((n) => nodeToExpressionText(n, options)).join(", ");
      return `function(${params})`;
    }
    case "inline": {
      const children: NodeJson[] = [];
      // pre-process children
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i];

        // special handling for e and e^(...)
        if (
          options?.normalizeExp &&
          child.type === "constant" &&
          child.children[0]?.type === "symbol" &&
          child.children[0]?.props.key === "constant-eulers-number"
        ) {
          // replace e with exp(1) and e^(...) with exp(...)
          const next = node.children[i + 1];
          if (isPowerOperator(next)) {
            // e^(...)
            const powerChild = next.children[0];
            children.push({
              type: "function",
              props: { key: "function-exponential-function", isBuiltIn: true },
              children: [
                {
                  type: "symbol",
                  props: { value: "exp" },
                  children: [],
                },
                powerChild,
              ],
            });
            i += 1; // skip next
          } else {
            // e
            children.push({
              type: "function",
              props: { key: "function-exponential-function", isBuiltIn: true },
              children: [
                {
                  type: "symbol",
                  props: { value: "exp" },
                  children: [],
                },
                {
                  type: "inline",
                  props: {},
                  children: [
                    {
                      type: "number",
                      props: { value: "1" },
                      children: [],
                    },
                  ],
                },
              ],
            });
          }
        } else {
          children.push(child);
        }
      }

      const params = children.map((n) => nodeToExpressionText(n, options)).join(", ");
      return `inline(${params})`;
    }
    case "plainText": {
      return `text(${node.props.value})`;
    }
    case "matrix": {
      const rows = node.children
        .map((row) => {
          const cols = row.children.map((col) => nodeToExpressionText(col, options)).join(",");
          return `row(${cols})`;
        })
        .join(", ");
      return `matrix(${rows})`;
    }
    case "decorator": {
      return node.props.name;
    }
    case "unit": {
      const contentNode = node.children.find((n) => n.type === "inline");
      return contentNode ? `unit(${nodeToExpressionText(contentNode, options)})` : "";
    }
    default:
      return "";
  }
}
